import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const SearchForm = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  min-width: 200px;
  flex: 1;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 4px;
`;

const StatusBadge = styled.span<{ $isActive?: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#4caf50" : "#ffc107")};
  color: white;
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background-color: transparent;
  color: #013566;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #013566;
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: #013566;
  font-size: 0.9rem;
`;

const CreateButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #013566;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #012a4a;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;
  const token = localStorage.getItem("token");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchClients = useCallback(
    async (page: number) => {
      if (!token) return;
      const response = await axios.get(API_ENDPOINTS.CLIENTS, {
        params: {
          page,
          per_page: itemsPerPage,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setClients(response.data?.data || []);
      setCurrentPage(response.data?.current_page ?? page);
      setTotalPages(response.data?.last_page ?? 1);
    },
    [token, itemsPerPage],
  );

  useEffect(() => {
    fetchClients(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, fetchClients]);

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // API returns the current page already; keep search as a client-side filter on that page
  const currentClients = filteredClients;

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleEdit = (client: any) => {
    navigate(`/clients/${client.id}/edit`, {
      state: { clientData: client },
    });
  };

  console.log(clients);
  return (
    <Container>
      <PageTitle>Client Management</PageTitle>

      <SearchForm>
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          type="text"
          placeholder="Search by client name..."
        />
        <CreateButton onClick={() => navigate("/clients/create")}>
          Create Client
        </CreateButton>
      </SearchForm>

      <TilesSection>
        <Tile>
          <TileHeader>
            <h2>ID</h2>
            <h2>Client Name</h2>
            <h2>Phone</h2>
            <h2>Logo</h2>
            <h2>Status</h2>
            <h2>Actions</h2>
          </TileHeader>

          <TileSection>
            {currentClients.map((item) => (
              <TileRow key={item.id}>
                <TileElement>{item.id}</TileElement>
                <TileElement>{item.name}</TileElement>
                <TileElement>{item.phone || "-"}</TileElement>
                <TileElement>
                  <LogoImage src={item.logo_url} alt={item.name} />
                </TileElement>
                <TileElement>
                  <StatusBadge $isActive={item.status === 1}>
                    {item.status === 1 ? "Active" : "Inactive"}
                  </StatusBadge>
                </TileElement>
                <TileElement>
                  <EditButton onClick={() => handleEdit(item)}>Edit</EditButton>
                </TileElement>
              </TileRow>
            ))}
          </TileSection>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton
                $disabled={currentPage <= 1}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                ← Previous
              </PaginationButton>
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
              <PaginationButton
                $disabled={currentPage >= totalPages}
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next →
              </PaginationButton>
            </PaginationContainer>
          )}
        </Tile>
      </TilesSection>
    </Container>
  );
};

export default Clients;
